"use client";

/**
 * `<PlansSection>` — wraps both Reports (3 tiers) and Compass (2 tiers)
 * with a SHARED billing state. Toggling Monthly/Annual in either
 * section's header re-renders both sections in sync.
 *
 * Pricing locked per dispatch Phase 5:
 *
 * Reports:
 *   - Free      $0           (3 reports/month)
 *   - Pro       $9.99 / $99.99 annual   (Save $19.89 ≈ 17%)
 *   - Premium   $19.99 monthly only — annual "Coming soon"
 *
 * Compass:
 *   - Pro       $29 / $290 annual       (Save $58 ≈ 17%)
 *   - Premium   $79 / $790 annual       (Save $158 ≈ 17%)
 *
 * "Coming in V2" features (Compass Premium only, dispatch-locked):
 *   - Topic Discovery Engine
 *   - Thesis Tracking
 *   - Position Management
 *   - Structural event alerts
 *   - Earnings-call transcript analysis
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Container, Eyebrow } from "../primitives";
import { Reveal } from "../reveal";
import { BillingToggle, type Billing } from "./billing-toggle";
import { PlanCard } from "./plan-card";
import { trackEvent } from "@/lib/posthog";

type CompassPlan =
  | "pro-monthly"
  | "pro-annual"
  | "premium-monthly"
  | "premium-annual";
type ReportsInterval = "monthly" | "yearly";

/**
 * Calls one of the Stripe checkout endpoints and either follows the
 * Stripe-hosted URL or sends the user through a signup detour if not
 * authed. The detour brings them back here with `?autoCheckout=<plan>`
 * so the checkout fires automatically after OAuth.
 */
/**
 * RM-W4-03 refusals. Returns true when the response was a guard refusal and
 * has been handled: an existing subscriber goes to the billing portal
 * (OR-33); a checkout still being set up or completed gets a plain message.
 */
async function handleCheckoutRefusal(res: Response): Promise<boolean> {
  if (res.status !== 409 && res.status !== 503) return false;
  const data = (await res.json().catch(() => ({}))) as { error?: string; portalUrl?: string | null };
  if (data.error === "already_subscribed") {
    if (data.portalUrl) {
      window.location.href = data.portalUrl;
    } else {
      alert("You already have this subscription. Manage it from your account page.");
    }
    return true;
  }
  if (data.error === "checkout_completing") {
    alert("Your payment went through and your subscription is being set up. Please refresh in a minute.");
    return true;
  }
  if (data.error === "checkout_in_progress" || data.error === "checkout_pending_retry") {
    alert("A checkout for this plan is already being set up. Please wait a moment and try again.");
    return true;
  }
  return false;
}

async function startCompassCheckout(plan: CompassPlan): Promise<void> {
  trackEvent("stripe_checkout_started", { product: "compass", plan });
  const res = await fetch("/api/stripe/compass/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan }),
  });

  if (res.status === 401) {
    const data = await res.json().catch(() => ({}));
    // Round-trip: signup → return to /pricing with autoCheckout param.
    const callback = encodeURIComponent(`/pricing?autoCheckout=${plan}#compass`);
    window.location.href =
      data.redirect ?? `/signup?callbackUrl=${callback}`;
    return;
  }
  if (await handleCheckoutRefusal(res)) return;
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error("[compass-checkout] failed", res.status, text);
    alert("Something went wrong starting checkout. Please try again.");
    return;
  }
  const data = await res.json();
  if (data?.url) window.location.href = data.url as string;
}

async function startReportsCheckout(interval: ReportsInterval): Promise<void> {
  trackEvent("stripe_checkout_started", { product: "reports", plan: interval });
  const res = await fetch("/api/stripe/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interval }),
  });

  if (res.status === 401) {
    const callback = encodeURIComponent(
      `/pricing?autoCheckoutReports=${interval}#reports`,
    );
    window.location.href = `/signup?callbackUrl=${callback}`;
    return;
  }
  if (await handleCheckoutRefusal(res)) return;
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error("[reports-checkout] failed", res.status, text);
    alert("Something went wrong starting checkout. Please try again.");
    return;
  }
  const data = await res.json();
  if (data?.url) window.location.href = data.url as string;
}

export function PlansSection() {
  const [billing, setBilling] = useState<Billing>("monthly");
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const searchParams = useSearchParams();

  const triggerCompass = useCallback(async (plan: CompassPlan) => {
    setPendingPlan(plan);
    try {
      await startCompassCheckout(plan);
    } finally {
      // window.location.href has already navigated away on success;
      // clearing pendingPlan is only relevant if the checkout failed
      // and we stayed on this page.
      setPendingPlan(null);
    }
  }, []);

  const triggerReports = useCallback(async (interval: ReportsInterval) => {
    setPendingPlan(`reports-${interval}`);
    try {
      await startReportsCheckout(interval);
    } finally {
      setPendingPlan(null);
    }
  }, []);

  // Fire pricing_view once on mount. PostHog auto-pageview already
  // tracks the URL hit; this is the funnel-specific event that
  // distinguishes "pricing page rendered" from a generic pageview
  // (e.g., bots, prefetch, deep-links to anchor sections).
  useEffect(() => {
    trackEvent("pricing_view");
  }, []);

  // Post-signup auto-resume: if the URL carries ?autoCheckout=<plan>
  // (set by the 401 branch in startCompassCheckout before bouncing to
  // /signup), immediately call the API again — now that the user has
  // a session, it'll succeed and redirect to Stripe.
  useEffect(() => {
    const autoCompass = searchParams.get("autoCheckout");
    const autoReports = searchParams.get("autoCheckoutReports");
    if (
      autoCompass === "pro-monthly" ||
      autoCompass === "pro-annual" ||
      autoCompass === "premium-monthly" ||
      autoCompass === "premium-annual"
    ) {
      void triggerCompass(autoCompass);
      return;
    }
    if (autoReports === "monthly" || autoReports === "yearly") {
      void triggerReports(autoReports);
    }
  }, [searchParams, triggerCompass, triggerReports]);

  return (
    <>
      <ReportsBlock
        billing={billing}
        onChange={setBilling}
        onSubscribe={triggerReports}
        pendingPlan={pendingPlan}
      />
      <CompassBlock
        billing={billing}
        onChange={setBilling}
        onSubscribe={triggerCompass}
        pendingPlan={pendingPlan}
      />
    </>
  );
}

function ReportsBlock({
  billing,
  onChange,
  onSubscribe,
  pendingPlan,
}: {
  billing: Billing;
  onChange: (b: Billing) => void;
  onSubscribe: (interval: ReportsInterval) => void | Promise<void>;
  pendingPlan: string | null;
}) {
  const proPrice =
    billing === "annual" ? 99.99 / 12 : 9.99; // effective monthly
  const proSuffix = billing === "annual" ? "/mo · billed yearly" : "/mo";
  const proFootnote =
    billing === "annual"
      ? "$99.99 billed annually, save $19.89"
      : undefined;

  return (
    <section
      id="reports"
      style={{
        padding: "80px 0",
        background: "var(--color-bg-2)",
        borderTop: "1px solid var(--color-line-2)",
        borderBottom: "1px solid var(--color-line-2)",
        scrollMarginTop: 120,
      }}
    >
      <Container max={1320}>
        <div
          className="flex flex-wrap items-end justify-between gap-6"
          style={{ marginBottom: 40 }}
        >
          <Reveal>
            <div style={{ maxWidth: 640 }}>
              <Eyebrow color="var(--color-muted)" className="mb-3">
                WTM Reports
              </Eyebrow>
              <h2
                style={{
                  fontSize: "clamp(28px, 3vw, 38px)",
                  fontWeight: 600,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.1,
                  margin: 0,
                  color: "var(--color-text)",
                }}
              >
                On-demand deep research, when you need it.
              </h2>
              <p
                style={{
                  fontSize: 15,
                  color: "var(--color-muted)",
                  lineHeight: 1.6,
                  marginTop: 14,
                }}
              >
                Each Report runs the 9-phase framework against a single
                ticker. Free tier covers casual use; Pro adds depth and
                throughput.
              </p>
            </div>
          </Reveal>
          <BillingToggle value={billing} onChange={onChange} />
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3"
          style={{ gap: 16 }}
        >
          <Reveal>
            <PlanCard
              name="Free"
              tag="Get familiar"
              price={0}
              suffix="/mo"
              cta="Start free"
              ctaHref="/signup"
              features={[
                "3 reports / month",
                "9-phase analysis",
                "Public methodology",
                "Email export",
              ]}
              missing={[
                "Unlimited reports",
                "Peer-comparison view",
              ]}
            />
          </Reveal>
          <Reveal delay={100}>
            <PlanCard
              name="Pro"
              tag="For active research"
              price={proPrice}
              formatPrice={(p) => p.toFixed(2)}
              suffix={proSuffix}
              priceFootnote={proFootnote}
              featured
              cta="Start Pro"
              onCtaClick={() =>
                onSubscribe(billing === "annual" ? "yearly" : "monthly")
              }
              ctaPending={
                pendingPlan ===
                `reports-${billing === "annual" ? "yearly" : "monthly"}`
              }
              features={[
                "Unlimited reports",
                "9-phase analysis",
                "Peer-comparison view",
                "Email · PDF export",
                "Watchlist seed",
                "Priority support",
              ]}
            />
          </Reveal>
          <Reveal delay={200}>
            <PlanCard
              name="Premium"
              tag="For power users"
              price={billing === "annual" ? null : 19.99}
              formatPrice={(p) => p.toFixed(2)}
              suffix="/mo"
              comingSoon={billing === "annual"}
              priceFootnote={
                billing === "annual"
                  ? "Annual billing for Premium arrives later"
                  : undefined
              }
              cta={billing === "annual" ? "Coming soon" : "Join waitlist"}
              ctaHref={
                billing === "annual" ? undefined : "/signup?plan=reports-premium"
              }
              features={[
                "Everything in Pro",
                "Deeper synthesis on Premium briefs",
                "CSV export",
                "Priority refresh queue",
                "Beta · API access",
              ]}
            />
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function CompassBlock({
  billing,
  onChange,
  onSubscribe,
  pendingPlan,
}: {
  billing: Billing;
  onChange: (b: Billing) => void;
  onSubscribe: (plan: CompassPlan) => void | Promise<void>;
  pendingPlan: string | null;
}) {
  const proPrice = billing === "annual" ? 290 / 12 : 29;
  const premPrice = billing === "annual" ? 790 / 12 : 79;
  const periodSuffix =
    billing === "annual" ? "/mo · billed yearly" : "/mo";
  const proFootnote =
    billing === "annual"
      ? "$290 billed annually, save $58"
      : undefined;
  const premFootnote =
    billing === "annual"
      ? "$790 billed annually, save $158"
      : undefined;

  return (
    <section
      id="compass"
      style={{ padding: "80px 0", scrollMarginTop: 120 }}
    >
      <Container max={1180}>
        <div
          className="flex flex-wrap items-end justify-between gap-6"
          style={{ marginBottom: 40 }}
        >
          <Reveal>
            <div style={{ maxWidth: 640 }}>
              <Eyebrow className="mb-3">
                WTM Compass · Flagship
              </Eyebrow>
              <h2
                style={{
                  fontSize: "clamp(28px, 3vw, 38px)",
                  fontWeight: 600,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.1,
                  margin: 0,
                  color: "var(--color-text)",
                }}
              >
                Subscribe once. Compass runs forever.
              </h2>
              <p
                style={{
                  fontSize: 15,
                  color: "var(--color-muted)",
                  lineHeight: 1.6,
                  marginTop: 14,
                }}
              >
                Pro is the sweet spot for most active investors. Premium
                adds depth, priority refresh, and unlocks the V2 feature
                set when it ships.
              </p>
            </div>
          </Reveal>
          <BillingToggle value={billing} onChange={onChange} />
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2"
          style={{ gap: 16 }}
        >
          <Reveal>
            <PlanCard
              name="Compass Pro"
              nameColor="var(--color-blue-2)"
              tag="For most active investors"
              price={proPrice}
              formatPrice={(p) => p.toFixed(0)}
              suffix={periodSuffix}
              priceFootnote={proFootnote}
              featured
              badge="MOST POPULAR"
              cta="Start Compass Pro"
              onCtaClick={() =>
                onSubscribe(billing === "annual" ? "pro-annual" : "pro-monthly")
              }
              ctaPending={
                pendingPlan ===
                (billing === "annual" ? "pro-annual" : "pro-monthly")
              }
              features={[
                "2 Active stocks (deep weekly briefs)",
                "15 Monitor stocks (5 core analyzers)",
                "Unlimited Watchlist",
                "Weekly Sunday digest email",
                "Catalyst alerts",
                "Conviction history tracking",
                "PDF download",
              ]}
              missing={[
                "Priority refresh queue",
                "V2 feature set",
              ]}
            />
          </Reveal>
          <Reveal delay={120}>
            <PlanCard
              name="Compass Premium"
              nameColor="var(--color-gold)"
              tag="For serious portfolios"
              price={premPrice}
              formatPrice={(p) => p.toFixed(0)}
              suffix={periodSuffix}
              priceFootnote={premFootnote}
              cta="Start Premium"
              onCtaClick={() =>
                onSubscribe(
                  billing === "annual" ? "premium-annual" : "premium-monthly",
                )
              }
              ctaPending={
                pendingPlan ===
                (billing === "annual" ? "premium-annual" : "premium-monthly")
              }
              features={[
                "5 Active stocks (deep weekly briefs)",
                "30 Monitor stocks (5 core analyzers)",
                "Unlimited Watchlist",
                "Weekly Sunday digest email",
                "Catalyst alerts",
                "Conviction history tracking",
                "PDF download",
                "Priority refresh queue",
              ]}
              v2Header="Coming in V2, included in Premium"
              v2Features={[
                "Topic Discovery Engine",
                "Thesis Tracking",
                "Position Management",
                "Structural event alerts",
                "Earnings-call transcript analysis",
              ]}
            />
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
